import { standardFormat, UTCFormatNumeric } from '@/constants/dateFormats';
import { OrmKeyInfoValueDate, OrmKeyInfoValueDateCurrency } from 'model/ApiGateway';

type FormatterArgs<T> = {
  item: T;
  currencySign: string;
  priceCurrencyCode: string;
  localizeDate: (dateAsString: string, options?: Intl.DateTimeFormatOptions) => string;
  formatCurrency: (
    number: number | string,
    currency?: string,
    notation?: string,
    fractionDigits?: number
  ) => number;
  formatNumber: (number: number | string, displayType?: string, fractionDigits?: number) => number;
  t: (key: string) => string;
};

type Formatter<T = string> = (args: FormatterArgs<T>) => string | number;

const identity: Formatter = ({ item }) => item;
const formatDate: Formatter = ({ item, localizeDate }) => localizeDate(item, standardFormat);
const formatUTCDate: Formatter = ({ item, localizeDate }) => localizeDate(item, UTCFormatNumeric);
const formatCompactCurrency: Formatter = ({
  item,
  priceCurrencyCode,
  currencySign,
  formatCurrency,
}) => `${currencySign} ${formatCurrency(item, priceCurrencyCode, 'compact')}`;
const formatStandardCurrency: Formatter = ({ item, priceCurrencyCode, formatCurrency }) =>
  formatCurrency(item, priceCurrencyCode, 'standard');
const formatStandardCurrency6: Formatter = ({ item, priceCurrencyCode, formatCurrency }) =>
  formatCurrency(item, priceCurrencyCode, 'standard', 6);
const formatPercentage: Formatter = ({ item, formatNumber }) => formatNumber(item, 'percentage');
const formatDecimal2: Formatter = ({ item, formatNumber }) => formatNumber(item, 'decimal', 2);
const formatNumberWithoutDecimals: Formatter<number> = ({ item }) => Math.trunc(item);
const formatAsOf: Formatter<OrmKeyInfoValueDate> = ({
  item,
  currencySign,
  priceCurrencyCode,
  formatCurrency,
  localizeDate,
  t,
}) => {
  if (!item.value) return t('notAvailableShortMessage');
  return `${currencySign} ${formatCurrency(item.value ?? '', priceCurrencyCode, 'compact')} ${t(
    'asOf'
  )} ${localizeDate(item.date ?? '')}`;
};
const notAvailableFormatter: Formatter = ({ t }) => t('notAvailableShortMessage');

type DictionaryItem<T = any> = {
  name: string;
  type:
    | 'date'
    | 'string'
    | 'number'
    | 'percentage'
    | 'object'
    | 'numberWithStandardNotation'
    | 'numberWithoutDecimals'
    | 'UTCDate';
  format: Formatter<T>;
};

const dictionaryItems: Record<string, DictionaryItem> = {
  shareClassLaunchDate: {
    name: 'fundDetailsKeyInfoShareClassLaunchDate',
    type: 'date',
    format: formatDate,
  },
  benchmark: { name: 'fundDetailsKeyInfoBenchmark', type: 'string', format: identity },
  underlyingYield: {
    name: 'fundDetailsKeyInfoUnderlyingYield',
    type: 'number',
    format: formatCompactCurrency,
  },
  distributionYield: {
    name: 'fundDetailsKeyInfoDistributionYield',
    type: 'number',
    format: formatCompactCurrency,
  },
  historicYield: {
    name: 'fundDetailsKeyInfoHistoricYield',
    type: 'number',
    format: formatCompactCurrency,
  },
  managementCosts: {
    name: 'fundDetailsKeyInfoManagementCost',
    type: 'percentage',
    format: formatPercentage,
  },
  baseCurrency: { name: 'fundDetailsKeyInfoBaseCurrency', type: 'string', format: identity },
  managementFee: {
    name: 'fundDetailsKeyInfoManagementFee',
    type: 'percentage',
    format: formatPercentage,
  },
  frontEndLoad: {
    name: 'fundDetailsKeyInfoFrontEndLoad',
    type: 'percentage',
    format: formatPercentage,
  },
  fundLaunchDate: { name: 'fundDetailsKeyInfoFundLaunchDate', type: 'date', format: formatDate },
  fundSize: { name: 'fundDetailsKeyInfoFundSize', type: 'number', format: formatCompactCurrency },
  fundSizeWithDateCurrency: {
    name: 'fundDetailsKeyInfoFundSize',
    type: 'object',
    format: ({ item, ...rest }) => formatAsOf({ item, ...rest, currencySign: item.currency! }),
  } as DictionaryItem<OrmKeyInfoValueDateCurrency>,
  domicile: { name: 'fundDetailsKeyInfoDomicile', type: 'string', format: identity },
  trustee: { name: 'fundDetailsKeyInfoTrustee', type: 'string', format: identity },
  registrar: { name: 'fundDetailsKeyInfoRegistrar', type: 'string', format: identity },
  auditor: { name: 'fundDetailsKeyInfoAuditor', type: 'string', format: identity },
  fundType: { name: 'fundDetailsKeyInfoFundType', type: 'string', format: identity },
  buySellSpread: { name: 'fundDetailsKeyInfoBuySellSpread', type: 'string', format: identity },
  highestNav: { name: 'fundDetailsKeyInfoHighestNav', type: 'object', format: formatAsOf },
  lowestNav: { name: 'fundDetailsKeyInfoLowestNav', type: 'object', format: formatAsOf },
  minInitialInvestment: {
    name: 'fundDetailsKeyInfoMinInitialInvestment',
    type: 'string',
    format: identity,
  },
  performanceTarget: {
    name: 'fundDetailsKeyInfoPerformanceTarget',
    type: 'string',
    format: identity,
  },
  performanceComparator: {
    name: 'fundDetailsKeyInfoPerformanceComparator',
    type: 'string',
    format: identity,
  },
  portfolioConstrainingBenchmark: {
    name: 'fundDetailsKeyInfoPortfolioConstrainingBenchmark',
    type: 'string',
    format: identity,
  },
  entryCharge: { name: 'fundDetailsKeyInfoEntryCharge', type: 'string', format: identity },
  valuationPoint: { name: 'fundDetailsKeyInfoValuationPoint', type: 'string', format: identity },
  switchingFee: {
    name: 'fundDetailsKeyInfoSwitchingFee',
    type: 'percentage',
    format: formatPercentage,
  },
  fundIncludedUnder: {
    name: 'fundDetailsKeyInfoFundIncludedUnder',
    type: 'string',
    format: identity,
  },
  numberOfHoldings: {
    name: 'fundDetailsKeyInfoNumberOfHoldings',
    type: 'string',
    format: identity,
  },
  wal: { name: 'fundDetailsKeyInfoWal', type: 'string', format: identity },
  wam: { name: 'fundDetailsKeyInfoWam', type: 'string', format: identity },
  dailyAmcPerCent: {
    name: 'fundDetailsKeyInfoDailyAmcPerCent',
    type: 'percentage',
    format: formatPercentage,
  },
  accountType: { name: 'fundDetailsKeyInfoAccountType', type: 'string', format: identity },
  custodian: { name: 'fundDetailsKeyInfoCustodian', type: 'string', format: identity },
  investmentManager: {
    name: 'fundDetailsKeyInfoInvestmentManager',
    type: 'string',
    format: identity,
  },
  isaEligibility: { name: 'fundDetailsKeyInfoIsaEligibility', type: 'string', format: identity },
  issuingCompany: { name: 'fundDetailsKeyInfoIssuingCompany', type: 'string', format: identity },
  managementApproach: {
    name: 'fundDetailsKeyInfoManagementApproach',
    type: 'string',
    format: identity,
  },
  managementCompany: {
    name: 'fundDetailsKeyInfoManagementCompany',
    type: 'string',
    format: identity,
  },
  rebalanceFrequency: {
    name: 'fundDetailsKeyInfoRebalanceFrequency',
    type: 'string',
    format: identity,
  },
  sfdrClassification: {
    name: 'fundDetailsKeyInfoSfdrClassification',
    type: 'string',
    format: identity,
  },
  sippAvailable: { name: 'fundDetailsKeyInfoSippAvailable', type: 'string', format: identity },
  isin: { name: 'fundDetailsKeyInfoIsin', type: 'string', format: identity },
  netAssets: {
    name: 'fundDetailsKeyInfoNetAssets',
    type: 'numberWithStandardNotation',
    format: formatStandardCurrency,
  },
  shareClassCurrency: {
    name: 'fundDetailsKeyInfoShareClassCurrency',
    type: 'string',
    format: identity,
  },
  sharesOutstanding: {
    name: 'fundDetailsKeyInfoSharesOutstanding',
    type: 'numberWithoutDecimals',
    format: formatNumberWithoutDecimals,
  },
  taxStatus: { name: 'fundDetailsKeyInfoTaxStatus', type: 'string', format: identity },
  totalExpenseRatio: {
    name: 'fundDetailsKeyInfoTotalExpenseRatio',
    type: 'percentage',
    format: formatPercentage,
  },
  ucits: { name: 'fundDetailsKeyInfoUcits', type: 'string', format: identity },
  useOfIncome: { name: 'fundDetailsKeyInfoUseOfIncome', type: 'string', format: identity },
  amcPerCent: {
    name: 'fundDetailsKeyInfoAmcPerCent',
    type: 'percentage',
    format: formatPercentage,
  },
  netExpenseRatio: {
    name: 'fundDetailsKeyInfoNetExpenseRatio',
    type: 'percentage',
    format: formatPercentage,
  },
  currencyHedged: { name: 'fundDetailsKeyInfoCurrencyHedged', type: 'string', format: identity },
  dividendFrequency: {
    name: 'fundDetailsKeyInfoDividendFrequency',
    type: 'string',
    format: identity,
  },
  exposure: { name: 'fundDetailsKeyInfoExposure', type: 'string', format: identity },
  assetClass: { name: 'fundDetailsKeyInfoAssetClass', type: 'string', format: identity },
  legalStructure: { name: 'fundDetailsKeyInfoLegalStructure', type: 'string', format: identity },
  grossExpenseRatio: {
    name: 'fundDetailsKeyInfoGrossExpenseRatio',
    type: 'numberWithStandardNotation',
    format: formatStandardCurrency,
  },
  ticker: { name: 'fundDetailsKeyInfoTicker', type: 'string', format: identity },
  cusip: { name: 'fundDetailsKeyInfoCusip', type: 'string', format: identity },
  fundManager: { name: 'fundDetailsKeyInfoFundManager', type: 'string', format: identity },
  inceptionDate: { name: 'fundDetailsKeyInfoInceptionDate', type: 'date', format: formatDate },
  netAssetValue: {
    name: 'fundDetailsKeyInfoNetAssetValue',
    type: 'number',
    format: formatCompactCurrency,
  },
  primaryBenchmark: {
    name: 'fundDetailsKeyInfoPrimaryBenchmark',
    type: 'string',
    format: identity,
  },
  sevenDayNetYield: {
    name: 'fundDetailsKeyInfoSevenDayNetYield',
    type: 'number',
    format: formatCompactCurrency,
  },
  m2mVsAmortised: {
    name: 'fundDetailsKeyInfoM2mVsAmortised',
    type: 'number',
    format: formatStandardCurrency6,
  },
  medianBidAskSpread: {
    name: 'fundDetailsKeyInfoMedianBidAskSpread',
    type: 'percentage',
    format: formatPercentage,
  },
  basketSizeGold: {
    name: 'fundDetailsKeyInfoBasketSizeGold',
    type: 'number',
    format: formatAsOf,
  },
  basketSizePalladium: {
    name: 'fundDetailsKeyInfoBasketSizePalladium',
    type: 'number',
    format: formatAsOf,
  },
  basketSizePlatinum: {
    name: 'fundDetailsKeyInfoBasketSizePlatinum',
    type: 'number',
    format: formatAsOf,
  },
  basketSizeSilver: {
    name: 'fundDetailsKeyInfoBasketSizeSilver',
    type: 'number',
    format: formatAsOf,
  },
  cefTickerSymbol: { name: 'fundDetailsKeyInfoCefTickerSymbol', type: 'string', format: identity },
  cefSharesOutstanding: {
    name: 'fundDetailsKeyInfoCefSharesOutstanding',
    type: 'string',
    format: identity,
  },
  cefManagedAssets: {
    name: 'fundDetailsKeyInfoCefManagedAssets',
    type: 'string',
    format: identity,
  },
  cefNetAssets: { name: 'fundDetailsKeyInfoCefNetAssets', type: 'string', format: identity },
  cefFiscalYearEnd: {
    name: 'fundDetailsKeyInfoCefFiscalYearEnd',
    type: 'string',
    format: identity,
  },
  cefMarketDistributionRate: {
    name: 'fundDetailsKeyInfoCefMarketDistributionRate',
    type: 'string',
    format: identity,
  },
  cefNavDistributionRate: {
    name: 'fundDetailsKeyInfoCefNavDistributionRate',
    type: 'string',
    format: identity,
  },
  cefGrossExpenseRatio: {
    name: 'fundDetailsKeyInfoCefGrossExpenseRatio',
    type: 'string',
    format: identity,
  },
  cefNetExpenseRatio: {
    name: 'fundDetailsKeyInfoCefNetExpenseRatio',
    type: 'string',
    format: identity,
  },
  iopvValue: { name: 'fundDetailsKeyInfoIopvValue', type: 'number', format: formatDecimal2 },
  iopvDate: { name: 'fundDetailsKeyInfoIopvDate', type: 'UTCDate', format: formatUTCDate },
  sdrClassification: {
    name: 'fundDetailsKeyInfoSdrClassification',
    type: 'string',
    format: identity,
  },
  sdrLabel: { name: 'fundDetailsKeyInfoSdrLabel', type: 'string', format: identity },
  fundSizeWithDate: { name: 'fundDetailsAum', type: 'UTCDate', format: formatAsOf },
  shareClassDividendFrequency: {
    name: 'fundDetailsKeyInfoShareClassDividendFrequency',
    type: 'string',
    format: identity,
  },
  isHedged: { name: 'fundDetailsKeyInfoIsHedged', type: 'string', format: identity },
};

const getDictionaryItemFormatter = ({ key, item }: { key: string; item: any }): Formatter<any> => {
  const def = dictionaryItems[key];
  if (!item || !def) {
    return notAvailableFormatter;
  }
  return def.format;
};

export default dictionaryItems;
export { getDictionaryItemFormatter };
